/**
 * @documental
 * @export useRemoteTableDataSource
 * @import
 * import { useRemoteTableDataSource } from "capitalrx-components-2.0/lib/general";
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { keepPreviousData, useQuery } from "@tanstack/react-query";
import axios from "axios";
import stringify from "json-stable-stringify";
import { DeepReadonly } from "ts-essentials";

import { useAuthTokenContext } from "..";
import { EmptyArray } from "../../utilities/EmptyArray";
import { EmptyObject } from "../../utilities/EmptyObject";
import { identity } from "../../utilities/identity";
import { useAlert } from "../Alert";
import { Batchable } from "../Batchable";
import { DataId } from "../DataId";
import { DataRow } from "../DataRow";
import { DataSource } from "../DataSource";
import { FilterMethods, FilterSet, Filterable } from "../Filterable";
import { Indexable } from "../Indexable";
import { Paged } from "../Paged";
import { SortMethods, Sortable } from "../Sortable";
import { BaseDataSourceState } from "./BaseDataSourceState";
import { DataSourceSortEntry } from "./DataSourceSortEntry";
import { RemoteDataErrorHandler } from "./RemoteDataErrorHandler";
import { TableDataSourceOptions } from "./TableDataSourceOptions";
import { TableIdProvider } from "./TableIdProvider";
import { TableRowTransform } from "./TableRowTransform";
import { useSharedDataSourceFunctions } from "./useSharedDataSourceFunctions";

/**
 * The fallback maximum row count in case we can't parse it at all.
 */
const MAX_ROWS_FALLBACK = 2300;

/**
 * The data source initial state.
 */

const getInitialState = <TRow extends object>({
  options,
}: {
  options: RemoteTableDataSourceOptions<TRow> | IndexedRemoteTableDataSourceOptions<TRow> | undefined;
}) => ({
  rows: EmptyArray as ReadonlyArray<DataRow<TRow>>,
  page: 0,
  pageSize: options?.pageSize ?? 20,
  hasNextPage: false,
  totalRows: 0,
  maxRows: 0,
});

interface RemoteTableData<TRow extends object, TMetadata extends object = {}> {
  /**
   * The number of items in the data set.
   */
  readonly result_count: string;

  /**
   * The result items.
   */
  readonly results: ReadonlyArray<TRow>;

  /**
   * The metadata associated with the remote table data.
   */
  readonly metadata?: DeepReadonly<TMetadata>;
}

/**
 * A refetch callback.
 */
export interface RefetchCallback<TRow extends object> {
  /**
   * Returns the refetch interval for the specified data.
   * @param data The remote table data.
   */
  (data?: RemoteTableData<TRow>): number | false;
}

/**
 * The internal data source state.
 */
type State<TRow extends object> = BaseDataSourceState<TRow>;

/**
 * Remote table data source options.
 */
export interface RemoteTableDataSourceOptions<TRow extends object> extends TableDataSourceOptions {
  /**
   * Specifies how refetching occurs on query mount.
   */
  readonly refetchOnMount?: boolean | "always";

  /**
   * Specifies how refetching occurs on window focus.
   */
  readonly refetchOnWindowFocus?: boolean | "always";

  /**
   * Specifies how refetching occurs on query reconnecting.
   */
  readonly refetchOnReconnect?: boolean | "always";

  /**
   * Specifies the data refetch interval.
   */
  readonly refetchInterval?: number | false | RefetchCallback<TRow>;

  /**
   * Specifies the time in milliseconds after data is considered stale.
   */
  readonly staleTime?: number | typeof Infinity;

  /**
   * Indicates whether the datasource is enabled or not.
   */
  readonly enabled?: boolean | ((filters: FilterSet) => boolean);

  /**
   * The function to call if an error occurs during the remote request.
   */
  readonly onError?: RemoteDataErrorHandler;

  /**
   * The authentication token to use for API requests. If not provided, requests will be made without authentication.
   */
  readonly authToken?: string | null;

  /**
   * When true (default), disables the query if no auth token is available.
   * Set to false for endpoints that do not require authentication.
   */
  readonly authTokenRequired?: boolean;
}

/**
 * Indexed Remote table data source options.
 */
export interface IndexedRemoteTableDataSourceOptions<TRow extends object> extends RemoteTableDataSourceOptions<TRow> {
  /**
   * The filter name of the ID field. Used when performing a call to the `get` function. If not specified calls to `get` will
   * output an error to the console and the result will be `null`.
   */
  readonly idFilterName: string;
}

/**
 * Provides a remote table data source.
 *
 * @param url The URL to the remote data source.
 * @param rowTransform The row transformation function.
 * @param idProvider A function which returns the unique identifier for a row.
 * @param options Pagination options config object.
 * @returns A table data source which provides access to the row data.
 */
export function useRemoteTableDataSource<TRemoteRow extends object, TRow extends object = TRemoteRow>(
  url: string,
  rowTransform: TableRowTransform<TRemoteRow, TRow>,
  idProvider: TableIdProvider<TRow>,
): DataSource<TRow> & Paged & Filterable & Sortable & Batchable<FilterMethods & SortMethods>;

/**
 * Provides a remote table data source.
 *
 * @param url The URL to the remote data source.
 * @param idProvider A function which returns the unique identifier for a row.
 * @param options Pagination options config object.
 * @returns A table data source which provides access to the row data.
 */
export function useRemoteTableDataSource<TRow extends object>(
  url: string,
  idProvider: TableIdProvider<TRow>,
): DataSource<TRow> & Paged & Filterable & Sortable & Batchable<FilterMethods & SortMethods>;

/**
 * Provides a remote table data source.
 *
 * @param url The URL to the remote data source.
 * @param rowTransform The row transformation function.
 * @param idProvider A function which returns the unique identifier for a row.
 * @param options Pagination options config object.
 * @returns A table data source which provides access to the row data.
 */
export function useRemoteTableDataSource<TRemoteRow extends object, TRow extends object = TRemoteRow>(
  url: string,
  rowTransform: TableRowTransform<TRemoteRow, TRow>,
  idProvider: TableIdProvider<TRow>,
  options: IndexedRemoteTableDataSourceOptions<TRow>,
): DataSource<TRow> & Paged & Filterable & Sortable & Indexable<TRow> & Batchable<FilterMethods & SortMethods>;

/**
 * Provides a remote table data source.
 *
 * @param url The URL to the remote data source.
 * @param idProvider A function which returns the unique identifier for a row.
 * @param options Pagination options config object.
 * @returns A table data source which provides access to the row data.
 */
export function useRemoteTableDataSource<TRow extends object>(
  url: string,
  idProvider: TableIdProvider<TRow>,
  options: IndexedRemoteTableDataSourceOptions<TRow>,
): DataSource<TRow> & Paged & Filterable & Sortable & Indexable<TRow> & Batchable<FilterMethods & SortMethods>;

/**
 * Provides a remote table data source.
 *
 * @param url The URL to the remote data source.
 * @param rowTransform The row transformation function.
 * @param idProvider A function which returns the unique identifier for a row.
 * @param options Pagination options config object.
 * @returns A table data source which provides access to the row data.
 */
export function useRemoteTableDataSource<TRemoteRow extends object, TRow extends object = TRemoteRow>(
  url: string,
  rowTransform: TableRowTransform<TRemoteRow, TRow>,
  idProvider: TableIdProvider<TRow>,
  options: RemoteTableDataSourceOptions<TRow>,
): DataSource<TRow> & Paged & Filterable & Sortable & Batchable<FilterMethods & SortMethods>;

/**
 * Provides a remote table data source.
 *
 * @param url The URL to the remote data source.
 * @param idProvider A function which returns the unique identifier for a row.
 * @param options Pagination options config object.
 * @returns A table data source which provides access to the row data.
 */
export function useRemoteTableDataSource<TRow extends object>(
  url: string,
  idProvider: TableIdProvider<TRow>,
  options: RemoteTableDataSourceOptions<TRow>,
): DataSource<TRow> & Paged & Filterable & Sortable & Batchable<FilterMethods & SortMethods>;

/**
 * Provides a remote table data source.
 *
 * @param url The URL to the remote data source.
 * @param idProvider A function which returns the unique identifier for a row.
 * @param options Pagination options config object.
 * @returns A table data source which provides access to the row data.
 */
export function useRemoteTableDataSource<TRow extends object>(
  url: string,
  ...args: [
    TableRowTransform<unknown, TRow> | TableIdProvider<TRow>,
    (TableIdProvider<TRow> | RemoteTableDataSourceOptions<TRow> | IndexedRemoteTableDataSourceOptions<TRow>)?,
    (RemoteTableDataSourceOptions<TRow> | IndexedRemoteTableDataSourceOptions<TRow>)?,
  ]
): DataSource<TRow> & Paged & Filterable & Sortable & Partial<Indexable<TRow>> & Batchable<FilterMethods & SortMethods> {
  let rowTransform: TableRowTransform<unknown, TRow>;
  let idProvider: TableIdProvider<TRow>;
  let options: RemoteTableDataSourceOptions<TRow> | IndexedRemoteTableDataSourceOptions<TRow> | undefined;

  // Parse out parameters...
  if (args.length === 3) {
    rowTransform = args[0] as TableRowTransform<unknown, TRow>;
    idProvider = args[1] as TableIdProvider<TRow>;
    options = args[2];
  } else if (args.length === 2) {
    if (args[1] && typeof args[1] === "function") {
      rowTransform = args[0] as TableRowTransform<unknown, TRow>;
      idProvider = args[1] as TableIdProvider<TRow>;
      options = undefined;
    } else {
      rowTransform = identity as TableRowTransform<unknown, TRow>;
      idProvider = args[0] as TableIdProvider<TRow>;
      options = args[1] as RemoteTableDataSourceOptions<TRow>;
    }
  } else {
    rowTransform = identity as TableRowTransform<unknown, TRow>;
    idProvider = args[0] as TableIdProvider<TRow>;
    options = undefined;
  }

  const showAlert = useAlert();
  const contextAuthToken = useAuthTokenContext();
  // Use explicitly provided authToken, fall back to context, then null
  const authToken = options?.authToken ?? contextAuthToken ?? null;
  const filterName = (options as IndexedRemoteTableDataSourceOptions<TRow> | undefined)?.idFilterName;

  const sharedDataSourceFunctions = useSharedDataSourceFunctions({
    sort: options?.sort || (EmptyArray as ReadonlyArray<DataSourceSortEntry>),
    filter: options?.filter || (EmptyObject as FilterSet),
  });

  const refetchInterval = options?.refetchInterval;

  const initialState = getInitialState({ options });

  const [internalState, setInternalState] = useState<State<TRow>>(initialState);

  const skipQueryRef = useRef(false);

  const sortString = stringify(sharedDataSourceFunctions.sort);
  const filterString = stringify(sharedDataSourceFunctions.filter);

  const optionsFilterString = stringify(options?.filter || EmptyObject);

  // Sync options.filter with internal filter state
  useEffect(() => {
    const newFilter = options?.filter || (EmptyObject as FilterSet);
    const currentFilter = sharedDataSourceFunctions.filter;

    if (stringify(newFilter) !== stringify(currentFilter)) {
      sharedDataSourceFunctions.setFilter(newFilter);
    }
  }, [optionsFilterString]);

  useEffect(() => {
    setInternalState((current) => {
      if (current.page === 0) return current;

      // Signal that the query should not update this current render cycle.
      skipQueryRef.current = true;

      return {
        ...current,
        page: 0,
      };
    });
  }, [filterString]);

  let enabled: boolean;
  if (typeof options?.enabled == "function") {
    enabled = options.enabled(sharedDataSourceFunctions.filter);
  } else {
    enabled = options?.enabled === false ? false : !skipQueryRef.current;
  }

  // Disable query when auth is required but token is missing (e.g., during logout)
  if (options?.authTokenRequired !== false && !authToken) {
    enabled = false;
  }

  const { data, status, isFetching, isPending, refetch } = useQuery<unknown, Error, RemoteTableData<TRow>>({
    queryKey: [url, authToken, internalState.page, internalState.pageSize, sortString, filterString],
    queryFn: async () => {
      const client = axios.create({
        headers: {
          "X-Referrer-Url": window.location.href,
        },
      });

      if (authToken) {
        client.defaults.headers.common = {
          Authorization: `Bearer ${authToken}`,
        };
      }

      const page = internalState.page + 1;

      try {
        const result = await client({
          url,
          method: "POST",
          data: {
            ...sharedDataSourceFunctions.filter,
            page_number: page,
            results_per_page: internalState.pageSize,
            order_by: sharedDataSourceFunctions.sort.map((entry) => ({ [entry.name]: entry.direction === "ascending" ? "asc" : "desc" })),
          },
        });

        return result.data as RemoteTableData<TRow>;
      } catch (err) {
        const errorHandler = options?.onError;

        if (errorHandler) {
          errorHandler(err);
          return undefined;
        }

        const message = typeof err === "string" || err instanceof Error ? err : "Error loading remote data.";

        showAlert({
          message,
          style: "banner",
          type: "error",
        });

        return undefined;
      }
    },
    enabled,
    staleTime: options?.staleTime,
    refetchInterval:
      typeof refetchInterval === "function"
        ? (query) => refetchInterval(query.state.data as RemoteTableData<TRow> | undefined)
        : refetchInterval,
    refetchOnMount: options?.refetchOnMount,
    refetchOnReconnect: options?.refetchOnReconnect,
    refetchOnWindowFocus: options?.refetchOnWindowFocus,
    placeholderData: keepPreviousData,
  });

  skipQueryRef.current = false;

  useEffect(() => {
    setInternalState((current) => {
      if (!data) {
        return {
          ...current,
          displayRows: [],
          page: 0,
          hasNextPage: false,
          sort: [],
          filter: {},
          totalRows: 0,
          pagingTotalRows: 0,
        };
      }

      const { result_count, results } = data;
      let totalRows = parseInt(result_count, 10);
      if (isNaN(totalRows)) totalRows = -1;

      const maxRowDigits = result_count.replace(/[^0-9]/g, "");
      let maxRows = parseInt(maxRowDigits, 10);
      if (isNaN(maxRows)) maxRows = MAX_ROWS_FALLBACK;

      let page = current.page;
      let startIndex = current.pageSize * page;
      if (startIndex > maxRows - 1) {
        page = Math.floor(maxRows / current.pageSize);
        startIndex = current.pageSize * page;
      }

      const hasNextPage = startIndex + current.pageSize < maxRows;

      const displayRows: Array<DataRow<TRow>> = [];
      for (let i = 0; i < results.length; i++) {
        const transformedRow = rowTransform(results[i], i);
        const id = idProvider(transformedRow);
        if (id === null || id === undefined) continue;
        displayRows.push({ id, data: transformedRow });
      }

      return {
        ...current,
        displayRows,
        totalRows,
        maxRows,
        hasNextPage,
        page,
        rows: displayRows,
      };
    });
  }, [data]);
  // We don't need to add a dep for the filter because data deps on filter, and we only want to make changes when data changes.

  const lookupTable = useMemo(() => {
    const entries = internalState.rows.map(({ id, data }) => [id, data]) as ReadonlyArray<[DataId, TRow]>;
    return new Map<DataId, TRow>(entries);
  }, [internalState.rows]);

  const get = useCallback(
    async (id: DataId | ReadonlyArray<DataId>): Promise<ReadonlyArray<DataRow<TRow>> | DataRow<TRow> | null> => {
      if (filterName === null || filterName === undefined) {
        console.error(
          "Attempting to call `get` with a remote table data source which does not have `isFilterName` set. Properly specify the name of the filter for the ID column associated with the rows for lookups.",
        );

        if (Array.isArray(id)) return EmptyArray;
        return null;
      }

      if (Array.isArray(id) && id.length === 0) return EmptyArray;

      const client = axios.create({
        headers: {
          "X-Referrer-Url": window.location.href,
        },
      });

      if (authToken) {
        client.defaults.headers.common = {
          Authorization: `Bearer ${authToken}`,
        };
      }

      const result = await client({
        url,
        method: "POST",
        data: {
          [filterName]: id,
          page_number: 1,
          results_per_page: Array.isArray(id) ? id.length : 1,
        },
      });

      const data = result.data as RemoteTableData<TRow>;

      if (Array.isArray(id)) {
        if (data.results.length === 0) return EmptyArray;

        return data.results.map((result) => {
          const transformedRow = rowTransform(result, 0);
          const rowId = idProvider(transformedRow);

          return {
            id: rowId,
            data: transformedRow,
          };
        });
      } else {
        if (data.results.length === 0) return null;

        const transformedRow = rowTransform(data.results[0], 0);
        const rowId = idProvider(transformedRow);

        return {
          id: rowId,
          data: transformedRow,
        };
      }
    },
    [url, authToken, filterName, rowTransform, idProvider, enabled],
  );

  const lookup = useCallback(
    (id: DataId) => {
      return lookupTable.get(id) ?? null;
    },
    [lookupTable],
  );

  const setPageSize = useCallback((pageSize: number) => {
    setInternalState((current) => ({
      ...current,
      pageSize,
    }));
  }, []);

  const first = useCallback(() => {
    setInternalState((current) => ({ ...current, page: 0 }));
  }, []);

  const next = useCallback(() => {
    setInternalState((current) => ({
      ...current,
      page: !current.hasNextPage ? current.page : current.page + 1,
    }));
  }, []);

  const previous = useCallback(() => {
    setInternalState((current) => ({
      ...current,
      page: current.page === 0 ? 0 : current.page - 1,
    }));
  }, []);

  const last = useCallback(() => {
    setInternalState((current) => ({
      ...current,
      page: !current.hasNextPage ? current.page : Math.ceil(current.maxRows / current.pageSize) - 1,
    }));
  }, []);

  const reload = useCallback(
    async (reset: boolean = false) => {
      if (reset) {
        setInternalState(initialState);
      } else {
        await refetch();
      }
    },
    [refetch],
  );

  return useMemo((): DataSource<TRow>
    & Paged
    & Filterable
    & Sortable
    & Partial<Indexable<TRow>>
    & Batchable<FilterMethods & SortMethods> => {
    const { filter, sort, ...sharedRest } = sharedDataSourceFunctions;
    return {
      ...internalState,
      ...sharedRest,
      enabled,
      metadata: data?.metadata ?? null,
      status: status === "pending" ? "initializing" : status,
      busy: isFetching,
      isPending,
      get: filterName === null || filterName === undefined ? undefined : (get as never), // can't seem to get the overload to go through, so just mark it like this.
      lookup,
      first,
      previous,
      next,
      last,
      setPageSize,
      reload,
    };
  }, [internalState, sharedDataSourceFunctions, status, enabled, isFetching, lookup, get, reload, filterName, data?.metadata]);
}
